import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Check, Sparkles, Tag, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import TermsAndConditions from '../components/TermsAndConditions';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const SignupWithPayment = ({ onAuth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('basic');
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    partner1_name: '',
    partner2_name: '',
  });

  const plans = {
    basic: {
      name: 'Essentials',
      price: 9.99,
      features: [
        'AI Wedding Timeline (Grace)',
        'Guest List Management',
        'Budget Planner',
        'Wedding Journey Checklist',
        'Guest Website Builder'
      ]
    },
    premium: {
      name: 'Premium',
      price: 14.99,
      features: [
        'Everything in Essentials',
        'AI Vendor Recommendations',
        'AI Budget Optimization',
        'Advanced Guest Analytics',
        'Priority Email Support',
        'Custom Vendor Vault'
      ]
    }
  };

  const validatePromoCode = async () => {
    if (!promoCode.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/auth/validate-promo`, { 
        code: promoCode,
        tier: selectedPlan  // Pass selected tier for validation
      });
      setPromoApplied(response.data);
      
      // Auto-select required tier if promo code has tier restriction
      if (response.data.allowed_tier && response.data.allowed_tier !== selectedPlan) {
        setSelectedPlan(response.data.allowed_tier);
        toast.info(`Plan switched to ${response.data.allowed_tier === 'basic' ? 'Essentials' : 'Premium'} (required for this promo)`);
      }
      
      if (response.data.trial_days > 0) {
        toast.success(`🎉 ${response.data.trial_days}-day free trial activated!`);
      } else if (response.data.discount_percent > 0) {
        toast.success(`💰 ${response.data.discount_percent}% discount applied!`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setPromoApplied(null);
    } finally {
      setValidatingPromo(false);
    }
  };

  const calculatePrice = () => {
    const basePrice = plans[selectedPlan].price;
    if (!promoApplied) return basePrice;
    
    if (promoApplied.trial_days > 0) return 0;
    
    const discount = (basePrice * promoApplied.discount_percent) / 100;
    return (basePrice - discount).toFixed(2);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupData.email || !signupData.password || !signupData.partner1_name || !signupData.partner2_name) {
      toast.error('Please fill in all fields');
      return;
    }

    if (signupData.password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (signupData.password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Please accept the Terms and Conditions to continue');
      return;
    }

    setLoading(true);
    
    // Retry logic for transient failures
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/signup-with-payment`, {
          ...signupData,
          subscription_tier: selectedPlan,
          promo_code: promoApplied ? promoCode : null
        }, {
          timeout: 30000 // 30 second timeout
        });

        // Check if this is a test code that skips payment
        if (response.data.skip_payment && response.data.user_id) {
          // Test account created - log them in directly
          const loginRes = await axios.post(`${API}/auth/login`, {
            email: signupData.email,
          password: signupData.password
        });
        onAuth(loginRes.data);
        toast.success(`Test account created! ${response.data.trial_days}-day access granted.`);
        navigate('/dashboard');
      } else {
        // All other signups go through Stripe (including regular trials for card verification)
        window.location.href = response.data.checkout_url;
      }
      return; // Success - exit the retry loop
      
    } catch (error) {
      lastError = error;
      
      // Only retry on network/timeout errors, not validation errors
      const isRetryable = error.code === 'ERR_NETWORK' || 
                          error.code === 'ECONNABORTED' ||
                          error.response?.status === 502 || 
                          error.response?.status === 503 ||
                          error.response?.status === 504;
      
      if (isRetryable && attempt < maxRetries) {
        console.log(`Signup attempt ${attempt + 1} failed, retrying...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1))); // Exponential backoff
        continue;
      }
      break; // Non-retryable error or max retries reached
    }
  }
    
    // If we get here, all retries failed
    console.error('Signup error:', lastError);
    console.error('Error response:', lastError?.response?.data);
    
    // Handle different error types with user-friendly messages
    let errorMessage = 'Signup failed. Please try again.';
    
    if (lastError?.code === 'ERR_NETWORK' || lastError?.message === 'Network Error') {
      errorMessage = 'Unable to connect. Please check your internet connection and try again.';
    } else if (lastError?.code === 'ECONNABORTED') {
      errorMessage = 'Request timed out. Please try again.';
    } else if (lastError?.response?.status === 502 || lastError?.response?.status === 503) {
      errorMessage = 'Our servers are temporarily busy. Please wait a moment and try again.';
    } else if (lastError?.response?.status === 504) {
      errorMessage = 'Request timed out. Please try again.';
    } else if (lastError?.response?.data?.detail) {
      errorMessage = lastError.response.data.detail;
    } else if (lastError?.message && !lastError?.message.includes('Request failed')) {
      errorMessage = lastError.message;
    }
    
    toast.error(errorMessage);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] py-12 px-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="text-[#881337]" size={40} fill="#881337" />
            <span className="text-4xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-[#57534E] text-lg">Choose your plan and start planning your perfect day</p>
        </div>

        {/* Plan Selection */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {Object.entries(plans).map(([key, plan]) => {
            // Check if plan is locked due to promo code restriction
            const isLockedByPromo = promoApplied && promoApplied.allowed_tier && promoApplied.allowed_tier !== key;
            
            return (
            <div
              key={key}
              onClick={() => {
                if (isLockedByPromo) {
                  toast.error(`Your promo code is only valid for the ${plans[promoApplied.allowed_tier].name} plan`);
                  return;
                }
                setSelectedPlan(key);
              }}
              className={`relative bg-white rounded-2xl p-8 transition-all duration-300 ${
                isLockedByPromo 
                  ? 'opacity-50 cursor-not-allowed ring-1 ring-stone-200'
                  : selectedPlan === key
                    ? 'ring-2 ring-[#881337] shadow-xl scale-105 cursor-pointer'
                    : 'ring-1 ring-stone-200 hover:ring-stone-300 shadow-md cursor-pointer'
              }`}
            >
              {key === 'premium' && (
                <div className="absolute -top-3 right-8 bg-[#881337] text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <Sparkles size={14} />
                  Most Popular
                </div>
              )}
              
              {isLockedByPromo && (
                <div className="absolute top-4 left-4 bg-stone-200 text-stone-600 px-3 py-1 rounded-full text-xs font-medium">
                  Not available with promo
                </div>
              )}
              
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-serif font-bold text-[#1C1917]">{plan.name}</h3>
                  <div className="flex items-baseline gap-1 mt-2">
                    <span className="text-3xl font-bold text-[#881337]">${plan.price}</span>
                    <span className="text-[#57534E]">/month</span>
                  </div>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  selectedPlan === key ? 'border-[#881337] bg-[#881337]' : 'border-stone-300'
                }`}>
                  {selectedPlan === key && <Check className="text-white" size={16} />}
                </div>
              </div>

              <ul className="space-y-3">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-[#57534E]">
                    <Check className="text-[#881337] flex-shrink-0 mt-0.5" size={18} />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )})}
        </div>

        {/* Signup Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
          <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-6">Create Your Account</h2>
          
          <form onSubmit={handleSignup} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1C1917] mb-2 block">Partner 1 Name</Label>
                <Input
                  value={signupData.partner1_name}
                  onChange={(e) => setSignupData({ ...signupData, partner1_name: e.target.value })}
                  placeholder="First partner's name"
                  required
                  className="border-stone-300"
                />
              </div>
              <div>
                <Label className="text-[#1C1917] mb-2 block">Partner 2 Name</Label>
                <Input
                  value={signupData.partner2_name}
                  onChange={(e) => setSignupData({ ...signupData, partner2_name: e.target.value })}
                  placeholder="Second partner's name"
                  required
                  className="border-stone-300"
                />
              </div>
            </div>

            <div>
              <Label className="text-[#1C1917] mb-2 block">Email</Label>
              <Input
                type="email"
                value={signupData.email}
                onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                placeholder="your@email.com"
                required
                className="border-stone-300"
              />
            </div>

            <div>
              <Label className="text-[#1C1917] mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={signupData.password}
                  onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                  placeholder="Create a secure password"
                  required
                  className="border-stone-300 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-[#1C1917] mb-2 block">Confirm Password</Label>
              <div className="relative">
                <Input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  required
                  className={`border-stone-300 pr-10 ${
                    confirmPassword && signupData.password !== confirmPassword 
                      ? 'border-red-500 focus:ring-red-500' 
                      : confirmPassword && signupData.password === confirmPassword
                        ? 'border-green-500 focus:ring-green-500'
                        : ''
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {confirmPassword && signupData.password !== confirmPassword && (
                <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
              )}
              {confirmPassword && signupData.password === confirmPassword && (
                <p className="text-green-600 text-sm mt-1">Passwords match ✓</p>
              )}
            </div>

            {/* Promo Code */}
            <div>
              <Label className="text-[#1C1917] mb-2 block flex items-center gap-2">
                <Tag size={16} />
                Promotional Code (Optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value.toUpperCase());
                    setPromoApplied(null);
                  }}
                  placeholder="Enter code for discounts or trials"
                  className="border-stone-300"
                />
                <Button
                  type="button"
                  onClick={validatePromoCode}
                  disabled={validatingPromo || !promoCode.trim()}
                  className="bg-stone-700 hover:bg-stone-800"
                >
                  {validatingPromo ? 'Checking...' : 'Apply'}
                </Button>
              </div>
              {promoApplied && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-700 font-medium">
                    ✅ {promoApplied.trial_days > 0 
                      ? `${promoApplied.trial_days}-day free trial activated!`
                      : `${promoApplied.discount_percent}% discount applied!`}
                  </p>
                  {promoApplied.allowed_tier && (
                    <p className="text-xs text-green-600 mt-1">
                      This promo is for the {promoApplied.allowed_tier === 'basic' ? 'Essentials' : 'Premium'} plan only
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Price Summary */}
            <div className="bg-stone-50 rounded-lg p-4 border border-stone-200">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#57534E]">{plans[selectedPlan].name} Plan</span>
                <span className="text-[#1C1917] font-medium">
                  {promoApplied && promoApplied.discount_percent > 0 && (
                    <span className="line-through text-stone-400 mr-2">${plans[selectedPlan].price}</span>
                  )}
                  ${calculatePrice()}/month
                </span>
              </div>
              {promoApplied && promoApplied.trial_days > 0 && (
                <div className="text-sm space-y-2">
                  {!promoApplied.skip_payment ? (
                    <>
                      {/* Required disclosures for promo codes */}
                      <div className="bg-[#881337]/5 rounded-lg p-3 border border-[#881337]/20">
                        <p className="text-[#881337] font-semibold mb-2">🎉 Free Month Activated!</p>
                        <ul className="space-y-1.5 text-stone-700 text-xs">
                          <li className="flex items-start gap-2">
                            <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>You won't be charged today.</strong></span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span>Your free month ends on <strong>{promoApplied.trial_end_date || 'the trial end date'}</strong>.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span>After your free month, {plans[selectedPlan].name} renews at <strong>${plans[selectedPlan].price}/month</strong> unless you cancel.</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <Check size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
                            <span><strong>A credit card is required</strong> to start your free month.</span>
                          </li>
                        </ul>
                      </div>
                      <p className="text-xs text-stone-500 italic">
                        You may upgrade to Premium at any time. If upgraded during your free month, Premium features unlock immediately and billing begins at the Premium rate after your free month ends.
                      </p>
                    </>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-[#881337] font-medium">
                        🎉 30 days FREE access in exchange for your feedback!
                      </p>
                      <p className="text-stone-600 text-xs">
                        No payment required. We'll check in with you periodically to hear about your experience and help us improve VowFlow.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 text-[#881337] border-stone-300 rounded focus:ring-[#881337]"
                />
                <label htmlFor="terms" className="text-sm text-stone-700">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-[#881337] font-medium hover:underline"
                  >
                    Terms and Conditions
                  </button>
                </label>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || !acceptedTerms}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : promoApplied && promoApplied.trial_days > 0 ? 'Start Free Trial' : 'Continue to Payment'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#57534E] text-sm">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/auth')}
                className="text-[#881337] font-medium hover:underline"
              >
                Log in
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Terms & Conditions Modal */}
      <TermsAndConditions
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={() => {
          setAcceptedTerms(true);
          setShowTermsModal(false);
          toast.success('Terms accepted');
        }}
      />
    </div>
  );
};

export default SignupWithPayment;
